import React, { useState, useEffect } from "react";
import { format, parse, startOfWeek, getDay, addHours, addDays } from "date-fns";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { enUS } from 'date-fns/locale';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { addDoc, collection, getFirestore, serverTimestamp, doc, getDoc, query, where, getDocs, updateDoc } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { Calendar as CalendarIcon, Clock, Clock4, MapPin, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar as DatePicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getEnrollmentsByUserId } from "@/utils/firestoreService";
import { useAuth } from "@/contexts/AuthContext";

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface ScheduleClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollmentId?: string;
  courseId?: string;
  hoursRemaining: number;
  onScheduled?: () => void;
}

interface Instructor {
  id: string;
  name: string;
  email: string;
}

interface InstructorAvailability {
  instructorEmail: string;
  unavailableSlots: {
    date: string;  // Format: "YYYY-MM-DD"
    startTime: string;  // Format: "HH:mm"
    endTime: string;  // Format: "HH:mm"
    isFullDay: boolean;
  }[];
}

const weekDays = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const formSchema = z.object({
  date: z.date({
    required_error: "Please select a date",
  }),
  time: z.string({
    required_error: "Please select a time slot",
  }),
  duration: z.string({
    required_error: "Please select a duration",
  }),
  venue: z.string()
    .min(3, "Venue must be at least 3 characters long")
    .max(100, "Venue must be less than 100 characters")
    .regex(/^[a-zA-Z0-9\s\-.,]+$/, "Venue can only contain letters, numbers, spaces, and basic punctuation"),
});

// Generate time slots in 15-minute intervals
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      slots.push(`${formattedHour}:${formattedMinute}`);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// Add utility functions at the top of the file
const parseTimeSafely = (time: string | undefined | null): Date | null => {
  if (!time) return null;
  try {
    // Validate time format (HH:mm)
    if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
      console.warn(`Invalid time format: ${time}`);
      return null;
    }
    return parse(time, "HH:mm", new Date());
  } catch (error) {
    console.error("Error parsing time:", error);
    return null;
  }
};

const formatTimeSafely = (date: Date | null): string | null => {
  if (!date) return null;
  try {
    return format(date, "HH:mm");
  } catch (error) {
    console.error("Error formatting time:", error);
    return null;
  }
};

const calculateEndTime = (startTime: Date | null, duration: number): Date | null => {
  if (!startTime) return null;
  try {
    return addHours(startTime, duration);
  } catch (error) {
    console.error("Error calculating end time:", error);
    return null;
  }
};

// Add this helper function at the top with other utility functions
const convertTimeToMinutes = (time: Date): number => {
  return time.getHours() * 60 + time.getMinutes();
};

const convertMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Add this helper function at the top with other utility functions
const findNextAvailableTime = (bookedSlots: any[], requestedDuration: number): string | null => {
  // Sort booked slots by start time
  const sortedSlots = [...bookedSlots].sort((a, b) => a.startMinutes - b.startMinutes);
  
  // Check each time slot
  for (let i = 0; i < timeSlots.length; i++) {
    const currentTime = parseTimeSafely(timeSlots[i]);
    if (!currentTime) continue;
    
    const startMinutes = convertTimeToMinutes(currentTime);
    const endMinutes = startMinutes + (requestedDuration * 60);
    
    // Check if this slot overlaps with any booked slots
    const hasOverlap = sortedSlots.some(slot => 
      (startMinutes >= slot.startMinutes && startMinutes < slot.endMinutes) ||
      (endMinutes > slot.startMinutes && endMinutes <= slot.endMinutes) ||
      (startMinutes <= slot.startMinutes && endMinutes >= slot.endMinutes)
    );
    
    if (!hasOverlap) {
      return timeSlots[i];
    }
  }
  
  return null;
};

const ScheduleClassDialog: React.FC<ScheduleClassDialogProps> = ({
  open,
  onOpenChange,
  enrollmentId,
  courseId,
  hoursRemaining,
  onScheduled,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>(timeSlots);
  const [instructorAvailability, setInstructorAvailability] = useState<InstructorAvailability | null>(null);
  const [assignedInstructor, setAssignedInstructor] = useState<Instructor | null>(null);
  const [instructorSchedule, setInstructorSchedule] = useState<any[]>([]);
  const [studentSchedule, setStudentSchedule] = useState<any[]>([]);
  const { toast } = useToast();
  const { currentUser } = useFirebase();
  const { user } = useAuth();
  const [availableDurations, setAvailableDurations] = useState<number[]>([1, 2, 3]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringWeeks, setRecurringWeeks] = useState(1);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [isCheckingRecurringAvailability, setIsCheckingRecurringAvailability] = useState(false);
  const [recurringAvailability, setRecurringAvailability] = useState<{[key: string]: string[]}>({});
  const [enrollment, setEnrollment] = useState<any>(null);
  const [actualHoursRemaining, setActualHoursRemaining] = useState<number>(0);
  const [hasOverlap, setHasOverlap] = useState(false);
  const [overlapMessage, setOverlapMessage] = useState("");
  const [nextAvailableTime, setNextAvailableTime] = useState<string | null>(null);
  const [isCarHire, setIsCarHire] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: undefined,
      time: "",
      duration: "1",
      venue: "",
    },
  });

  // Add validation function for date and time
  const validateDateTime = (date: Date | undefined, time: string) => {
    if (!date || !time) return true; // Let the form validation handle empty values
    
    const now = new Date();
    const selectedDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    selectedDateTime.setHours(hours, minutes, 0, 0);
    
    // If the date is today, check if the time is in the future
    if (format(selectedDateTime, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) {
      return selectedDateTime > now;
    }
    
    // For future dates, just check if the date is in the future
    return selectedDateTime > now;
  };

  // Fetch enrollment details when dialog opens
  useEffect(() => {
    const fetchEnrollmentDetails = async () => {
      if (!enrollmentId || !currentUser) return;
      
      try {
        const enrollments = await getEnrollmentsByUserId(currentUser.uid);
        const relevantEnrollment = enrollments.find(e => e.id === enrollmentId);
        if (relevantEnrollment) {
          setEnrollment(relevantEnrollment);
          setAssignedInstructor({
            id: relevantEnrollment.instructorId,
            name: relevantEnrollment.instructorName,
            email: relevantEnrollment.instructorEmail
          });
          // Calculate actual hours remaining
          const remaining = relevantEnrollment.hoursRemaining || 
            (relevantEnrollment.totalHours - (relevantEnrollment.hoursTaken || 0));
          setActualHoursRemaining(remaining);
        }
      } catch (error) {
        console.error("Error fetching enrollment details:", error);
        toast({
          title: "Error",
          description: "Failed to load enrollment details. Please try again.",
          variant: "destructive",
        });
      }
    };

    if (open) {
      fetchEnrollmentDetails();
    }
  }, [enrollmentId, currentUser, open, toast]);

  // Function to fetch instructor availability
  const fetchInstructorAvailability = async (instructorId: string) => {
    if (!instructorId || !assignedInstructor) {
      console.log('Missing instructor information');
      return;
    }

    try {
      const db = getFirestore();
      console.log('Fetching instructor availability for ID:', instructorId);
      
      // Get the instructor's availability document
      const availabilityDoc = await getDoc(doc(db, "instructor_availability", assignedInstructor.email));
      
      if (availabilityDoc.exists()) {
        const data = availabilityDoc.data();
        console.log('Raw instructor availability data:', data);
        
        if (!data) {
          console.log('No data in availability document');
          setInstructorAvailability({
            instructorEmail: assignedInstructor.email,
            unavailableSlots: []
          });
          return;
        }

        // Ensure the data structure is correct
        const availability: InstructorAvailability = {
          instructorEmail: assignedInstructor.email,
          unavailableSlots: Array.isArray(data.unavailableSlots) 
            ? data.unavailableSlots.filter((slot: any) => 
                slot && 
                typeof slot === 'object' && 
                typeof slot.date === 'string' && 
                typeof slot.startTime === 'string' &&
                typeof slot.endTime === 'string' &&
                typeof slot.isFullDay === 'boolean'
              )
            : []
        };
        
        console.log('Processed instructor availability data:', availability);
        setInstructorAvailability(availability);
      } else {
        console.log('No availability document found, creating new one');
        const defaultAvailability: InstructorAvailability = {
          instructorEmail: assignedInstructor.email,
          unavailableSlots: []
        };
        setInstructorAvailability(defaultAvailability);
      }
    } catch (error) {
      console.error("Error fetching instructor availability:", error);
      toast({
        title: "Error",
        description: "Failed to fetch instructor availability. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Update the checkInstructorAvailability function
  const checkInstructorAvailability = async (date: Date) => {
    if (!currentUser || !user) {
      console.log('User not authenticated');
      toast({
        title: "Error",
        description: "You must be logged in to check availability.",
        variant: "destructive",
      });
      return;
    }

    // Check if user has permission to check availability
    if (user.role !== 'student' && user.role !== 'admin' && user.role !== 'instructor') {
      console.log('User does not have permission to check availability:', user.role);
      toast({
        title: "Permission Denied",
        description: "You do not have permission to check availability.",
        variant: "destructive",
      });
      return;
    }

    if (!assignedInstructor) {
      console.log('No assigned instructor found, fetching...');
      // First fetch the enrollment to get the instructor details
      const db = getFirestore();
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      const enrollmentSnap = await getDoc(enrollmentRef);
      
      if (!enrollmentSnap.exists()) {
        console.error('No enrollment found');
        toast({
          title: "Error",
          description: "Could not find enrollment details. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const enrollment = enrollmentSnap.data();
      // Set the assigned instructor
      const instructor = {
        id: enrollment.instructorId,
        name: enrollment.instructorName,
        email: enrollment.instructorEmail
      };
      setAssignedInstructor(instructor);

      // Now fetch the instructor's availability
      await fetchInstructorAvailability(instructor.id);
    }

    setIsLoadingAvailability(true);
    setHasOverlap(false);
    setOverlapMessage("");
    
    try {
      const db = getFirestore();
      const formattedDate = format(date, "yyyy-MM-dd");
      console.log('Checking availability for date:', formattedDate);

      // Get instructor's availability
      const availabilityDoc = await getDoc(doc(db, "instructor_availability", assignedInstructor.email));
      const unavailableSlots = availabilityDoc.exists() 
        ? availabilityDoc.data()?.unavailableSlots || []
        : [];

      // Check if the entire day is marked as unavailable
      const isFullDayUnavailable = unavailableSlots.some((slot: any) => 
        slot.date === formattedDate && slot.isFullDay
      );

      if (isFullDayUnavailable) {
        console.log('Instructor is unavailable for the entire day');
        setAvailableTimeSlots([]);
        setHasOverlap(true);
        setOverlapMessage("The instructor is unavailable for the entire day. Please choose another date.");
        return;
      }

      // Filter unavailable times for the selected date
      const unavailableSlotsForDate = unavailableSlots
        .filter((slot: any) => 
          slot.date === formattedDate && 
          !slot.isFullDay && 
          slot.startTime && 
          slot.endTime
        );

      console.log('Unavailable slots for instructor on this date:', unavailableSlotsForDate);

      // Get all bookings for the selected date for this specific instructor
      const schedulesQuery = query(
        collection(db, "schedules"),
        where("date", "==", formattedDate),
        where("instructorId", "==", assignedInstructor.id),
        where("status", "in", ["pending", "confirmed"])
      );

      const schedulesSnapshot = await getDocs(schedulesQuery);
      console.log('Found', schedulesSnapshot.docs.length, 'existing bookings for instructor');

      const bookedSlots = schedulesSnapshot.docs.map(doc => {
        const data = doc.data();
        if (!data) return null;

        const startTime = parseTimeSafely(data.startTime);
        const endTime = parseTimeSafely(data.endTime);
        
        if (!startTime || !endTime) return null;

        const startMinutes = convertTimeToMinutes(startTime);
        const endMinutes = convertTimeToMinutes(endTime);

        return {
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          studentName: data.studentName || "Unknown Student",
          studentId: data.studentId,
          instructorId: data.instructorId,
          instructorName: data.instructorName,
          venue: data.venue || "Not specified",
          bookingId: doc.id
        };
      }).filter(Boolean);

      // Filter time slots based on booked times and instructor availability
      const availableSlots = timeSlots.filter(time => {
        if (!time) return false;
        
        const currentTime = parseTimeSafely(time);
        if (!currentTime) return false;

        const duration = parseInt(form.getValues("duration") || "1");
        const currentEnd = calculateEndTime(currentTime, duration);
        if (!currentEnd) return false;

        const startMinutes = convertTimeToMinutes(currentTime);
        const endMinutes = convertTimeToMinutes(currentEnd);

        // First check if the time slot is in the instructor's unavailable times
        const isUnavailable = unavailableSlotsForDate.some(unavailableSlot => {
          const unavailableStart = parseTimeSafely(unavailableSlot.startTime);
          const unavailableEnd = parseTimeSafely(unavailableSlot.endTime);
          if (!unavailableStart || !unavailableEnd) return false;

          const unavailableStartMinutes = convertTimeToMinutes(unavailableStart);
          const unavailableEndMinutes = convertTimeToMinutes(unavailableEnd);

          return (
            (startMinutes >= unavailableStartMinutes && startMinutes < unavailableEndMinutes) ||
            (endMinutes > unavailableStartMinutes && endMinutes <= unavailableEndMinutes) ||
            (startMinutes <= unavailableStartMinutes && endMinutes >= unavailableEndMinutes)
          );
        });

        if (isUnavailable) {
          console.log(`⏰ Time slot ${time} is marked as unavailable by instructor`);
          return false;
        }

        // Then check if the time slot overlaps with any booked slots for this instructor
        const hasOverlap = bookedSlots.some(slot => {
          if (!slot) return false;
          
          // Add buffer time to overlap check - instructors need 15-minute break between lessons
          const slotEndWithBuffer = slot.endMinutes + 15; // 15 minutes buffer
          
          const overlap = (
            (startMinutes < slotEndWithBuffer && endMinutes > slot.startMinutes)
          );

          if (overlap) {
            console.log(`🚨 Time slot ${time} overlaps with existing booking (with buffer):`, {
              existingBooking: {
                bookingId: slot.bookingId,
                studentName: slot.studentName,
                studentId: slot.studentId,
                timeRange: `${format(slot.startTime, 'HH:mm')} - ${format(slot.endTime, 'HH:mm')} (with 15-min buffer)`,
                startMinutes: slot.startMinutes,
                endMinutes: slot.endMinutes,
                endMinutesWithBuffer: slotEndWithBuffer
              },
              newBooking: {
                timeRange: `${format(currentTime, 'HH:mm')} - ${format(currentEnd, 'HH:mm')}`,
                startMinutes,
                endMinutes,
                duration
              }
            });
          }
          
          return overlap;
        });

        if (!hasOverlap) {
          console.log(`✅ Time slot ${time} is available`);
        }

        return !hasOverlap;
      });

      console.log('📋 Available time slots:', availableSlots);
      setAvailableTimeSlots(availableSlots);

      // Update the instructor and student schedules for display
      const instructorSchedules = bookedSlots
        .filter(slot => slot && slot.instructorId === assignedInstructor?.id)
        .map(slot => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
          studentName: slot.studentName,
          venue: slot.venue
        }));

      const studentSchedules = bookedSlots
        .filter(slot => slot && slot.studentId === currentUser?.uid)
        .map(slot => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
          instructorName: slot.instructorName,
          venue: slot.venue
        }));

      setInstructorSchedule(instructorSchedules);
      setStudentSchedule(studentSchedules);
      
      console.log('📊 Schedule Summary:', {
        instructorBookings: instructorSchedules.length,
        studentBookings: studentSchedules.length,
        availableSlots: availableSlots.length,
        totalTimeSlots: timeSlots.length
      });
      
      console.log('👨‍🏫 Found', instructorSchedules.length, 'bookings for instructor with other students');
      console.log('👨‍🎓 Found', studentSchedules.length, 'bookings for this student');

      // If the currently selected time is no longer available, reset it
      const currentTime = form.getValues("time");
      if (currentTime && !availableSlots.includes(currentTime)) {
        console.log('⚠️ Selected time slot is no longer available:', currentTime);
        form.setValue("time", "");
        setHasOverlap(true);
        setOverlapMessage("The selected time slot is no longer available. Please choose another time.");
      }

      // Show a message if no slots are available
      if (availableSlots.length === 0) {
        console.log('❌ No available slots found for the selected date');
        setHasOverlap(true);
        setOverlapMessage("No time slots are available for the selected date. Please choose another date.");
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      toast({
        title: "Error",
        description: "Failed to check time slot availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const checkAvailableDurations = async (selectedTime: string) => {
    if (!form.getValues("date") || !selectedTime || !assignedInstructor) return;

    const formattedDate = format(form.getValues("date"), "yyyy-MM-dd");
    const currentTime = parseTimeSafely(selectedTime);
    const db = getFirestore();
      const schedulesRef = collection(db, "schedules");
      
    // Get all schedules for the selected date for this specific instructor
    const schedulesQuery = query(
      schedulesRef,
      where("date", "==", formattedDate),
      where("instructorId", "==", assignedInstructor.id),
      where("status", "in", ["pending", "confirmed"])
    );

    getDocs(schedulesQuery).then(snapshot => {
      const bookedSlots = snapshot.docs.map(doc => {
        const data = doc.data();
        if (!data || !data.time) return null;
        try {
          const startTime = parseTimeSafely(data.time);
          const duration = data.duration || 1;
          const endTime = calculateEndTime(startTime, duration);
          return { startTime, duration, endTime };
        } catch (error) {
          console.error("Error parsing time:", error);
          return null;
        }
      }).filter(Boolean);

      // Check each possible duration
      const durations = [1, 2, 3].filter(duration => {
        try {
          const endTime = calculateEndTime(currentTime, duration);
          if (!endTime) return false;
          
          // Check if this duration would overlap with any booked slots
          const hasOverlap = bookedSlots.some(slot => {
            if (!slot || !slot.startTime || !slot.endTime) return false;
            
            // Add buffer time to overlap check - instructors need 15-minute break between lessons
            const slotEndWithBuffer = new Date(slot.endTime.getTime() + (15 * 60 * 1000)); // 15 minutes buffer
            
            return (
              (currentTime >= slot.startTime && currentTime < slotEndWithBuffer) ||
              (endTime > slot.startTime && endTime <= slotEndWithBuffer) ||
              (currentTime <= slot.startTime && endTime >= slotEndWithBuffer)
            );
          });

          // Check if the duration is within remaining hours
          const isWithinRemainingHours = duration <= actualHoursRemaining;

          return !hasOverlap && isWithinRemainingHours;
        } catch (error) {
          console.error("Error checking duration:", error);
          return false;
        }
      });

      setAvailableDurations(durations);
      
      // If current duration is not available, reset it
      if (durations.length > 0 && !durations.includes(parseInt(form.getValues("duration")))) {
        form.setValue("duration", durations[0].toString());
      }
    }).catch(error => {
      console.error("Error fetching schedules:", error);
      setAvailableDurations([]);
    });
  };

  // Watch for date changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "date" && value.date) {
        // Automatically select the day based on the chosen date
        const dayName = format(value.date, "EEEE").toLowerCase();
        if (isRecurring) {
          setSelectedDays([dayName]);
          // Update recurring availability when date changes
          checkRecurringAvailability();
        }
        checkInstructorAvailability(value.date);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, instructorAvailability, assignedInstructor, isRecurring]);

  // Watch for recurring toggle
  useEffect(() => {
    if (isRecurring && form.getValues("date")) {
      const dayName = format(form.getValues("date"), "EEEE").toLowerCase();
      setSelectedDays([dayName]);
      checkRecurringAvailability();
    } else {
      setSelectedDays([]);
      setRecurringAvailability({});
    }
  }, [isRecurring]);

  // Watch for time changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "time" && value.time) {
        const duration = parseInt(form.getValues("duration") || "1");
        checkForOverlap(value.time, duration);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Watch for duration changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "duration" && value.duration) {
        const duration = parseInt(value.duration || "1");
        checkForOverlap(value.time, duration);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Add useEffect to check if this is a car hire course
  useEffect(() => {
    const checkCarHire = async () => {
      if (enrollmentId) {
        try {
          const db = getFirestore();
          const enrollmentRef = doc(db, "enrollments", enrollmentId);
          const enrollmentDoc = await getDoc(enrollmentRef);
          if (enrollmentDoc.exists()) {
            const data = enrollmentDoc.data();
            setIsCarHire(data.courseName.toLowerCase().includes("car hire"));
            if (data.courseName.toLowerCase().includes("car hire")) {
              form.setValue("duration", "3");
            }
          }
        } catch (error) {
          console.error("Error checking car hire status:", error);
        }
      }
    };
    checkCarHire();
  }, [enrollmentId, form]);

  // Declare functions before using them
  const checkForOverlap = async (selectedTime: string, duration: number) => {
    if (!selectedTime || !form.getValues("date") || !assignedInstructor) return;

    const currentTime = parseTimeSafely(selectedTime);
    if (!currentTime) return;

    const endTime = calculateEndTime(currentTime, duration);
    if (!endTime) return;

    const startMinutes = convertTimeToMinutes(currentTime);
    const endMinutes = convertTimeToMinutes(endTime);

    try {
      const db = getFirestore();
      const formattedDate = format(form.getValues("date"), "yyyy-MM-dd");
      
      console.log('🔍 CHECKING FOR OVERLAPS:', {
        date: formattedDate,
        selectedTime,
        duration,
        startTime: format(currentTime, 'HH:mm'),
        endTime: format(endTime, 'HH:mm'),
        startMinutes,
        endMinutes,
        instructorId: assignedInstructor.id,
        instructorName: assignedInstructor.name
      });
      
      // Get all bookings for the selected date for this specific instructor
      const schedulesQuery = query(
        collection(db, "schedules"),
        where("date", "==", formattedDate),
        where("instructorId", "==", assignedInstructor.id),
        where("status", "in", ["pending", "confirmed"])
      );

      const schedulesSnapshot = await getDocs(schedulesQuery);
      console.log('📋 Found', schedulesSnapshot.docs.length, 'existing bookings for instructor on', formattedDate);

      const bookedSlots = schedulesSnapshot.docs.map(doc => {
        const data = doc.data();
        if (!data) return null;

        const startTime = parseTimeSafely(data.startTime);
        const endTime = parseTimeSafely(data.endTime);
        
        if (!startTime || !endTime) return null;

        const startMinutes = convertTimeToMinutes(startTime);
        const endMinutes = convertTimeToMinutes(endTime);

        return {
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          studentName: data.studentName || "Unknown Student",
          studentId: data.studentId,
          instructorId: data.instructorId,
          instructorName: data.instructorName,
          venue: data.venue || "Not specified",
          bookingId: doc.id
        };
      }).filter(Boolean);

      console.log('📅 Existing bookings:', bookedSlots.map(slot => ({
        bookingId: slot.bookingId,
        studentName: slot.studentName,
        studentId: slot.studentId,
        timeRange: `${format(slot.startTime, 'HH:mm')} - ${format(slot.endTime, 'HH:mm')}`,
        startMinutes: slot.startMinutes,
        endMinutes: slot.endMinutes
      })));

      let hasOverlap = false;
      let overlapDetails = null;

      for (const slot of bookedSlots) {
        if (!slot) continue;
        
        const overlap = (
          (startMinutes < slot.endMinutes && endMinutes > slot.startMinutes)
        );

        if (overlap) {
          hasOverlap = true;
          overlapDetails = {
            bookedStart: slot.startTime,
            bookedEnd: slot.endTime,
            studentName: slot.studentName,
            studentId: slot.studentId,
            bookingId: slot.bookingId,
            bookingStartMinutes: slot.startMinutes,
            bookingEndMinutes: slot.endMinutes,
            currentStartMinutes: startMinutes,
            currentEndMinutes: endMinutes,
            duration: duration
          };

          console.log('🚨 OVERLAP DETECTED in checkForOverlap:', {
            existingBooking: {
              bookingId: slot.bookingId,
              studentName: slot.studentName,
              studentId: slot.studentId,
              timeRange: `${format(slot.startTime, 'HH:mm')} - ${format(slot.endTime, 'HH:mm')}`,
              startMinutes: slot.startMinutes,
              endMinutes: slot.endMinutes
            },
            newBooking: {
              timeRange: `${format(currentTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`,
              startMinutes,
              endMinutes,
              duration
            },
            overlapType: 'TIME_SLOT_CONFLICT'
          });
          setOverlapMessage(`This time slot overlaps with an existing booking from ${format(slot.startTime, 'HH:mm')} to ${format(slot.endTime, 'HH:mm')} with ${slot.studentName}`);
          break; // Found an overlap, no need to check further
        }
      }

      if (!hasOverlap) {
        console.log('✅ No overlaps detected in checkForOverlap');
        setOverlapMessage("");
      }

      setHasOverlap(hasOverlap);

      if (hasOverlap) {
        const nextAvailableTime = findNextAvailableTime(bookedSlots, duration);
        setNextAvailableTime(nextAvailableTime);
        if (nextAvailableTime) {
          console.log('💡 Suggested next available time:', nextAvailableTime);
        }
      } else {
        setNextAvailableTime(null);
      }
    } catch (error) {
      console.error("❌ Error checking for overlaps:", error);
    }
  };

  const checkRecurringAvailability = async () => {
    if (!currentUser || !user) {
      console.log('User not authenticated');
      toast({
        title: "Error",
        description: "You must be logged in to check availability.",
        variant: "destructive",
      });
      return false;
    }

    // Check if user has permission to check availability
    if (user.role !== 'student' && user.role !== 'admin' && user.role !== 'instructor') {
      console.log('User does not have permission to check availability:', user.role);
      toast({
        title: "Permission Denied",
        description: "You do not have permission to check availability.",
        variant: "destructive",
      });
      return false;
    }

    const date = form.getValues("date");
    const time = form.getValues("time");
    const duration = form.getValues("duration");

    if (!date || !time || !duration || !assignedInstructor) {
      return false;
    }

    // If no days are selected, use the day of the selected date
    if (selectedDays.length === 0) {
      const dayName = format(date, "EEEE").toLowerCase();
      setSelectedDays([dayName]);
    }

    setIsCheckingRecurringAvailability(true);
    try {
      const db = getFirestore();
      const startDate = date;
      const endDate = addDays(startDate, (recurringWeeks - 1) * 7);
      const availability: {[key: string]: string[]} = {};
      let hasAvailableSlots = false;

      for (let week = 0; week < recurringWeeks; week++) {
        for (const day of selectedDays) {
          const lessonDate = addDays(startDate, week * 7 + weekDays.findIndex(d => d.value === day));
          const formattedDate = format(lessonDate, "yyyy-MM-dd");

          // Skip past dates
          if (lessonDate < new Date()) {
            availability[formattedDate] = [];
            continue;
          }

          // Get instructor's availability
          const availabilityDoc = await getDoc(doc(db, "instructor_availability", assignedInstructor.email));
          const unavailableSlots = availabilityDoc.exists() 
            ? availabilityDoc.data()?.unavailableSlots || []
            : [];

          // Check if the entire day is marked as unavailable
          const isFullDayUnavailable = unavailableSlots.some((slot: any) => 
            slot.date === formattedDate && slot.isFullDay
          );

          if (isFullDayUnavailable) {
            console.log(`Instructor is unavailable for the entire day on ${formattedDate}`);
            availability[formattedDate] = [];
            continue;
          }

          const unavailableSlotsForDate = unavailableSlots
            .filter((slot: any) => slot && slot.date === formattedDate && !slot.isFullDay);

          console.log(`Unavailable slots for ${formattedDate}:`, unavailableSlotsForDate);

          // Get instructor's existing bookings
          const instructorQuery = query(
            collection(db, "schedules"),
            where("instructorId", "==", assignedInstructor.id),
            where("date", "==", formattedDate),
            where("status", "in", ["pending", "confirmed"])
          );
          const instructorSchedules = await getDocs(instructorQuery);
          const instructorBookedSlots = instructorSchedules.docs.map(doc => {
            const data = doc.data();
            if (!data) return null;
            try {
              const startTime = data.startTime ? parseTimeSafely(data.startTime) : null;
              const endTime = data.endTime ? parseTimeSafely(data.endTime) : null;
              if (!startTime || !endTime) return null;
              return { startTime, endTime };
            } catch (error) {
              console.error("Error parsing instructor time:", error);
              return null;
            }
          }).filter(Boolean);

          // Get student's existing bookings
          const studentQuery = query(
            collection(db, "schedules"),
            where("studentId", "==", currentUser?.uid),
            where("date", "==", formattedDate),
            where("status", "in", ["pending", "confirmed"])
          );
          const studentSchedules = await getDocs(studentQuery);
          const studentBookedSlots = studentSchedules.docs.map(doc => {
            const data = doc.data();
            if (!data) return null;
            try {
              const startTime = data.startTime ? parseTimeSafely(data.startTime) : null;
              const endTime = data.endTime ? parseTimeSafely(data.endTime) : null;
              if (!startTime || !endTime) return null;
              return { startTime, endTime };
            } catch (error) {
              console.error("Error parsing student time:", error);
              return null;
            }
          }).filter(Boolean);

          // Check if the selected time slot is available
          try {
            const currentTime = parseTimeSafely(time);
            const currentEnd = calculateEndTime(currentTime, parseInt(duration));

            const isAvailable = !unavailableSlotsForDate.some(slot => {
              if (!slot || !slot.startTime || !slot.endTime) return false;
              
              const slotStart = parseTimeSafely(slot.startTime);
              const slotEnd = parseTimeSafely(slot.endTime);
              if (!slotStart || !slotEnd) return false;
              
              return (
                (currentTime >= slotStart && currentTime < slotEnd) ||
                (currentEnd > slotStart && currentEnd <= slotEnd) ||
                (currentTime <= slotStart && currentEnd >= slotEnd)
              );
            }) &&
              !instructorBookedSlots.some(slot => {
                if (!slot) return false;
                return (
                  (currentTime >= slot.startTime && currentTime < slot.endTime) ||
                  (currentEnd > slot.startTime && currentEnd <= slot.endTime) ||
                  (currentTime <= slot.startTime && currentEnd >= slot.endTime)
                );
              }) &&
              !studentBookedSlots.some(slot => {
                if (!slot) return false;
                return (
                  (currentTime >= slot.startTime && currentTime < slot.endTime) ||
                  (currentEnd > slot.startTime && currentEnd <= slot.endTime) ||
                  (currentTime <= slot.startTime && currentEnd >= slot.endTime)
                );
              });

            availability[formattedDate] = isAvailable ? [time] : [];
            if (isAvailable) hasAvailableSlots = true;
            
            console.log(`Checked availability for ${formattedDate}: Found ${instructorBookedSlots.length} instructor bookings, ${studentBookedSlots.length} student bookings`);
          } catch (error) {
            console.error("Error checking time slot availability:", error);
            availability[formattedDate] = [];
          }
        }
      }

      setRecurringAvailability(availability);
      return hasAvailableSlots;
    } catch (error) {
      console.error("Error checking recurring availability:", error);
      toast({
        title: "Error",
        description: "Failed to check recurring availability. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsCheckingRecurringAvailability(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!currentUser || !user) {
      console.log('User not authenticated for booking');
      toast({
        title: "Error",
        description: "You must be logged in to schedule a lesson.",
        variant: "destructive",
      });
      return;
    }

    // Check if user has permission to schedule
    if (user.role !== 'student' && user.role !== 'admin') {
      console.log('User does not have permission to schedule:', user.role);
      toast({
        title: "Permission Denied",
        description: "You do not have permission to schedule lessons.",
        variant: "destructive",
      });
      return;
    }

    // Check if user is trying to schedule for another user
    if (user.role === 'student' && currentUser.uid !== user.id) {
      console.log('Student trying to schedule for another user:', { currentUserUid: currentUser.uid, userId: user.id });
      toast({
        title: "Permission Denied",
        description: "You can only schedule lessons for yourself.",
        variant: "destructive",
      });
      return;
    }

    // Check if the requested duration exceeds remaining hours
    const requestedDuration = parseInt(values.duration);
    console.log('Checking duration:', { requestedDuration, actualHoursRemaining });
    
    if (requestedDuration > actualHoursRemaining) {
      console.log('Insufficient hours remaining');
      toast({
        title: "Insufficient Hours",
        description: `You only have ${actualHoursRemaining} hours remaining. Please select a shorter duration.`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const db = getFirestore();
      const schedulesRef = collection(db, "schedules");
      
      // Check if the time slot is available
      const formattedDate = format(values.date, "yyyy-MM-dd");
      const currentTime = parseTimeSafely(values.time);
      const endTime = calculateEndTime(currentTime, requestedDuration);
      
      if (!currentTime || !endTime) {
        console.log('Invalid time values:', { currentTime, endTime });
        throw new Error("Invalid time values");
      }

      // Convert times to minutes for comparison
      const startMinutes = convertTimeToMinutes(currentTime);
      const endMinutes = convertTimeToMinutes(endTime);

      console.log('=== BOOKING VALIDATION START ===');
      console.log('Student attempting to book:', {
        studentId: currentUser.uid,
        studentName: currentUser.displayName,
        date: formattedDate,
        startTime: format(currentTime, 'HH:mm'),
        endTime: format(endTime, 'HH:mm'),
        startMinutes,
        endMinutes,
        duration: requestedDuration,
        instructorId: assignedInstructor?.id,
        instructorName: assignedInstructor?.name
      });

      // Check for overlapping bookings with this specific instructor
      const schedulesQuery = query(
        schedulesRef,
        where("date", "==", formattedDate),
        where("instructorId", "==", assignedInstructor.id),
        where("status", "in", ["pending", "confirmed"])
      );

      const schedulesSnapshot = await getDocs(schedulesQuery);
      console.log('Found', schedulesSnapshot.docs.length, 'existing bookings for instructor on this date');

      const bookedSlots = schedulesSnapshot.docs.map(doc => {
        const data = doc.data();
        if (!data) return null;

        const startTime = parseTimeSafely(data.startTime);
        const endTime = parseTimeSafely(data.endTime);
        
        if (!startTime || !endTime) return null;

        const startMinutes = convertTimeToMinutes(startTime);
        const endMinutes = convertTimeToMinutes(endTime);

        return {
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          studentName: data.studentName || "Unknown Student",
          studentId: data.studentId,
          instructorId: data.instructorId,
          instructorName: data.instructorName,
          venue: data.venue || "Not specified",
          bookingId: doc.id
        };
      }).filter(Boolean);

      console.log('Existing bookings for instructor:', bookedSlots.map(slot => ({
        bookingId: slot.bookingId,
        studentName: slot.studentName,
        studentId: slot.studentId,
        startTime: format(slot.startTime, 'HH:mm'),
        endTime: format(slot.endTime, 'HH:mm'),
        startMinutes: slot.startMinutes,
        endMinutes: slot.endMinutes
      })));

      // Enhanced overlap checking with detailed logging
      let hasOverlap = false;
      let overlapDetails = null;

      for (const slot of bookedSlots) {
        if (!slot) continue;

        // Convert all times to minutes for easier comparison
        const slotStart = slot.startMinutes;
        const slotEnd = slot.endMinutes;
        const newStart = startMinutes;
        const newEnd = endMinutes;

        // Check for any kind of overlap by comparing start and end times
        const overlap = (
          // New booking starts before existing booking ends AND ends after existing booking starts
          (newStart < slotEnd && newEnd > slotStart)
        );

        if (overlap) {
          hasOverlap = true;
          overlapDetails = {
            bookedStart: slot.startTime,
            bookedEnd: slot.endTime,
            studentName: slot.studentName,
            studentId: slot.studentId,
            bookingId: slot.bookingId,
            bookingStartMinutes: slotStart,
            bookingEndMinutes: slotEnd,
            currentStartMinutes: newStart,
            currentEndMinutes: newEnd,
            duration: requestedDuration
          };

          console.log('🚨 OVERLAP DETECTED:', {
            existingBooking: {
              bookingId: slot.bookingId,
              studentName: slot.studentName,
              studentId: slot.studentId,
              startTime: format(slot.startTime, 'HH:mm'),
              endTime: format(slot.endTime, 'HH:mm'),
              timeRange: `${format(slot.startTime, 'HH:mm')} - ${format(slot.endTime, 'HH:mm')}`
            },
            newBooking: {
              studentName: currentUser.displayName,
              studentId: currentUser.uid,
              startTime: format(currentTime, 'HH:mm'),
              endTime: format(endTime, 'HH:mm'),
              timeRange: `${format(currentTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`
            },
            overlapType: 'TIME_SLOT_CONFLICT'
          });
          break; // Found an overlap, no need to check further
        }
      }

      if (hasOverlap) {
        console.log('❌ BOOKING REJECTED - Overlap detected');
        console.log('=== BOOKING VALIDATION END ===');
        
        const nextAvailableTime = findNextAvailableTime(bookedSlots, requestedDuration);
        
        let message = `The selected time slot overlaps with an existing booking from ${format(overlapDetails.bookedStart, 'HH:mm')} to ${format(overlapDetails.bookedEnd, 'HH:mm')} with ${overlapDetails.studentName}.`;
        if (nextAvailableTime) {
          message += ` Would you like to try ${nextAvailableTime} instead?`;
          // Update the form with the suggested time
          form.setValue("time", nextAvailableTime);
        } else {
          message += " Please choose another date as this day is fully booked.";
        }

        toast({
          title: "Time Slot Unavailable",
          description: message,
          variant: "destructive",
        });
        return;
      }

      // Additional validation: Check if student already has a booking at this time
      console.log('Checking for student double bookings...');
      const studentBookingsQuery = query(
        schedulesRef,
        where("date", "==", formattedDate),
        where("studentId", "==", currentUser.uid),
        where("status", "in", ["pending", "confirmed"])
      );

      const studentBookingsSnapshot = await getDocs(studentBookingsQuery);
      const studentBookings = studentBookingsSnapshot.docs.map(doc => {
        const data = doc.data();
        if (!data) return null;

        const startTime = parseTimeSafely(data.startTime);
        const endTime = parseTimeSafely(data.endTime);
        
        if (!startTime || !endTime) return null;

        const startMinutes = convertTimeToMinutes(startTime);
        const endMinutes = convertTimeToMinutes(endTime);

        return {
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          instructorName: data.instructorName || "Unknown Instructor",
          bookingId: doc.id
        };
      }).filter(Boolean);

      console.log('Student existing bookings:', studentBookings.map(booking => ({
        bookingId: booking.bookingId,
        instructorName: booking.instructorName,
        startTime: format(booking.startTime, 'HH:mm'),
        endTime: format(booking.endTime, 'HH:mm'),
        timeRange: `${format(booking.startTime, 'HH:mm')} - ${format(booking.endTime, 'HH:mm')}`
      })));

      // Check for student double bookings
      for (const booking of studentBookings) {
        const bookingStart = booking.startMinutes;
        const bookingEnd = booking.endMinutes;
        const newStart = startMinutes;
        const newEnd = endMinutes;

        const studentOverlap = (newStart < bookingEnd && newEnd > bookingStart);

        if (studentOverlap) {
          console.log('🚨 STUDENT DOUBLE BOOKING DETECTED:', {
            existingBooking: {
              bookingId: booking.bookingId,
              instructorName: booking.instructorName,
              startTime: format(booking.startTime, 'HH:mm'),
              endTime: format(booking.endTime, 'HH:mm'),
              timeRange: `${format(booking.startTime, 'HH:mm')} - ${format(booking.endTime, 'HH:mm')}`
            },
            newBooking: {
              instructorName: assignedInstructor.name,
              startTime: format(currentTime, 'HH:mm'),
              endTime: format(endTime, 'HH:mm'),
              timeRange: `${format(currentTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`
            },
            overlapType: 'STUDENT_DOUBLE_BOOKING'
          });

          console.log('❌ BOOKING REJECTED - Student double booking detected');
          console.log('=== BOOKING VALIDATION END ===');

          toast({
            title: "Double Booking Detected",
            description: `You already have a lesson scheduled from ${format(booking.startTime, 'HH:mm')} to ${format(booking.endTime, 'HH:mm')} with ${booking.instructorName}.`,
            variant: "destructive",
          });
          return;
        }
      }

      console.log('✅ ALL VALIDATIONS PASSED - No overlaps detected');
      console.log('=== BOOKING VALIDATION END ===');

      // Create the schedule with complete time range
      const scheduleData = {
        studentId: currentUser.uid,
        studentName: currentUser.displayName || "Student",
        studentEmail: currentUser.email,
        instructorId: assignedInstructor.id,
        instructorName: assignedInstructor.name,
        instructorEmail: assignedInstructor.email,
        enrollmentId: enrollmentId,
        date: formattedDate,
        startTime: values.time,
        endTime: formatTimeSafely(endTime),
        startMinutes: startMinutes,
        endMinutes: endMinutes,
        duration: requestedDuration,
        venue: values.venue,
        status: "confirmed",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isRecurring: false
      };

      console.log('🎉 CREATING NEW BOOKING:', {
        bookingId: 'PENDING',
        studentName: scheduleData.studentName,
        studentId: scheduleData.studentId,
        instructorName: scheduleData.instructorName,
        instructorId: scheduleData.instructorId,
        date: scheduleData.date,
        startTime: scheduleData.startTime,
        endTime: scheduleData.endTime,
        duration: scheduleData.duration,
        venue: scheduleData.venue
      });

      const newBookingRef = await addDoc(schedulesRef, scheduleData);
      
      console.log('✅ BOOKING CREATED SUCCESSFULLY:', {
        bookingId: newBookingRef.id,
        studentName: scheduleData.studentName,
        timeRange: `${scheduleData.startTime} - ${scheduleData.endTime}`,
        instructorName: scheduleData.instructorName
      });
      
      // Update the enrollment's hours taken
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      const enrollmentDoc = await getDoc(enrollmentRef);
      if (enrollmentDoc.exists()) {
        const enrollmentData = enrollmentDoc.data();
        const currentHoursTaken = enrollmentData.hoursTaken || 0;
        const newHoursTaken = currentHoursTaken + requestedDuration;
        const newHoursRemaining = enrollmentData.totalHours - newHoursTaken;
        
        await updateDoc(enrollmentRef, {
          hoursTaken: newHoursTaken,
          hoursRemaining: newHoursRemaining,
          updatedAt: serverTimestamp()
        });

        console.log('📊 ENROLLMENT UPDATED:', {
          enrollmentId,
          previousHoursTaken: currentHoursTaken,
          newHoursTaken,
          newHoursRemaining,
          totalHours: enrollmentData.totalHours
        });
      }
      
      toast({
        title: "Success",
        description: "Lesson has been scheduled successfully.",
      });
      
      form.reset();
      onOpenChange(false);
      if (onScheduled) {
        onScheduled();
      }
    } catch (error) {
      console.error("❌ ERROR SCHEDULING LESSON:", error);
      toast({
        title: "Error",
        description: "Failed to schedule the lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold">Schedule a Driving Lesson</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Select a date and time for your next driving lesson.
            {actualHoursRemaining > 0 && (
              <span className="block mt-2 text-drivewise-600 font-medium">
                You have {actualHoursRemaining} hours remaining in your package.
              </span>
            )}
            {assignedInstructor && (
              <span className="block mt-2 text-drivewise-600">
                Your assigned instructor: {assignedInstructor.name}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-base sm:text-lg font-semibold">Select Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          selected={field.value}
                          onChange={field.onChange}
                          minDate={new Date()}
                          maxDate={addDays(new Date(), 30)}
                          placeholder="Select a date"
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="venue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base sm:text-lg font-semibold">Lesson Venue</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter the meeting location"
                          className="w-full"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-sm">
                        Specify where you'd like to meet for the lesson
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base sm:text-lg font-semibold">Select Time</FormLabel>
                      {form.watch("date") && (
                        <div className="mb-2 text-xs sm:text-sm text-gray-600">
                          <p>Available time slots for {format(form.watch("date"), "EEEE, MMMM d, yyyy")}:</p>
                          {isLoadingAvailability ? (
                            <p className="font-medium text-blue-600">
                              Checking availability...
                            </p>
                          ) : availableTimeSlots.length > 0 ? (
                            <p className="font-medium text-green-600">
                              {availableTimeSlots.join(", ")}
                            </p>
                          ) : (
                            <p className="font-medium text-red-600">
                              No available time slots for this date
                            </p>
                          )}
                        </div>
                      )}
                        <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={!form.watch("date") || isLoadingAvailability}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a time" />
                          </SelectTrigger>
                        <SelectContent>
                            {availableTimeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base sm:text-lg font-semibold">Lesson Duration</FormLabel>
                      <FormControl>
                        {isCarHire ? (
                          <div className="text-lg font-semibold text-drivewise-600">
                            3 hours (Fixed duration for Car Hire)
                          </div>
                        ) : (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={!form.watch("time")}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableDurations.map((duration) => (
                                <SelectItem key={duration} value={duration.toString()}>
                                  {duration} {duration === 1 ? "hour" : "hours"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </FormControl>
                      <FormDescription className="text-sm">
                        {isCarHire ? "Car Hire lessons have a fixed duration of 3 hours" : "Choose how many hours you want to book"}
                      </FormDescription>
                      {parseInt(field.value || "0") > actualHoursRemaining && (
                        <div className="text-red-500 text-sm mt-1">
                          Insufficient hours remaining. You only have {actualHoursRemaining} hours left.
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={(checked) => {
                    setIsRecurring(checked as boolean);
                    if (!checked) {
                      setSelectedDays([]);
                      setRecurringWeeks(1);
                      setRecurringAvailability({});
                    }
                  }}
                />
                <Label htmlFor="recurring">Schedule Recurring Lessons</Label>
              </div>
              {isRecurring && (
                <div className="grid gap-4 pl-6 mt-2">
                  <div className="grid gap-2">
                    <Label>Number of Weeks</Label>
                    <Select
                      value={recurringWeeks.toString()}
                      onValueChange={(value) => setRecurringWeeks(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select number of weeks" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((weeks) => (
                          <SelectItem key={weeks} value={weeks.toString()}>
                            {weeks} {weeks === 1 ? "week" : "weeks"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>Select Days</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {weekDays.map((day) => (
                        <div key={day.value} className="flex items-center gap-2">
                          <Checkbox
                            id={day.value}
                            checked={selectedDays.includes(day.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDays([...selectedDays, day.value]);
                              } else {
                                setSelectedDays(selectedDays.filter(d => d !== day.value));
                              }
                            }}
                          />
                          <Label htmlFor={day.value}>{day.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedDays.length > 0 && form.watch("time") && (
                    <Button
                      variant="outline"
                      onClick={checkRecurringAvailability}
                      disabled={isCheckingRecurringAvailability}
                    >
                      {isCheckingRecurringAvailability ? "Checking Availability..." : "Check Recurring Availability"}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {form.watch("date") && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-4">Schedule for {format(form.watch("date"), "MMMM d, yyyy")}</h3>
                {studentSchedule.length > 0 ? (
                  <div className="space-y-3">
                    {studentSchedule.map((slot, index) => {
                      try {
                        if (!slot?.startTime || !slot?.endTime) return null;
                        const timeSlot = `${format(slot.startTime, "HH:mm")}-${format(slot.endTime, "HH:mm")}`;
                        const instructorName = slot.instructorName || "Not scheduled";
                        const venue = slot.venue || "Not specified";

                        return (
                          <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-md shadow-sm">
                            <div className="flex-1">
                              <div className="font-medium text-drivewise-900">{timeSlot}</div>
                              <div className="text-sm text-gray-600">
                                {currentUser?.displayName || "You"} with {instructorName}
                              </div>
                              <div className="text-sm text-gray-500">
                                <MapPin className="inline-block h-4 w-4 mr-1" />
                                {venue}
                              </div>
                            </div>
                          </div>
                        );
                      } catch (error) {
                        console.error("Error rendering student schedule:", error);
                        return null;
                      }
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No lessons scheduled for this date</p>
                )}
              </div>
            )}

            {form.watch("date") && form.watch("time") && (
              <div className="mt-4 p-4 bg-drivewise-50 rounded-lg border border-drivewise-200">
                <h3 className="font-semibold text-drivewise-900 mb-2">Selected Schedule</h3>
                <div className="flex items-center gap-2 text-drivewise-700">
                  <CalendarIcon className="h-5 w-5" />
                  <span>{format(form.watch("date"), "EEEE, MMMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-2 text-drivewise-700 mt-1">
                  <Clock className="h-5 w-5" />
                  <span>{form.watch("time")} ({form.watch("duration")} {form.watch("duration") === "1" ? "hour" : "hours"})</span>
                </div>
                {form.watch("venue") && (
                  <div className="flex items-center gap-2 text-drivewise-700 mt-1">
                    <MapPin className="h-5 w-5" />
                    <span>{form.watch("venue")}</span>
                  </div>
                )}
                {hasOverlap && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">{overlapMessage}</span>
                    </div>
                    {nextAvailableTime && (
                      <div className="mt-2 text-sm text-red-600">
                        Would you like to try {nextAvailableTime} instead?
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <DialogClose asChild>
                <Button variant="outline" type="button" className="w-full sm:w-auto">Cancel</Button>
              </DialogClose>
              <Button 
                type="submit" 
                disabled={
                  isSubmitting || 
                  isLoadingAvailability || 
                  availableTimeSlots.length === 0 ||
                  hasOverlap ||
                  parseInt(form.getValues("duration") || "0") > actualHoursRemaining ||
                  (isRecurring && (selectedDays.length === 0 || isCheckingRecurringAvailability))
                }
                className={cn(
                  "w-full sm:w-auto",
                  hasOverlap ? "bg-red-600 hover:bg-red-700" : "bg-drivewise-600 hover:bg-drivewise-700"
                )}
              >
                {isSubmitting ? "Scheduling..." : isRecurring ? "Schedule Recurring Lessons" : "Schedule Lesson"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleClassDialog;
